function uploadError(code, message, status = 0) {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
}

async function responseBody(response) {
  if (response.status === 204) return null;
  return response.json().catch(() => ({}));
}

export class CaptureUploader {
  constructor({
    store,
    fetchImpl = globalThis.fetch?.bind(globalThis),
    now = () => Date.now(),
    online = () => globalThis.navigator?.onLine !== false,
    onState = () => {}
  }) {
    this.store = store;
    this.fetchImpl = fetchImpl;
    this.now = now;
    this.online = online;
    this.onState = onState;
    this.authorization = null;
    this.drainPromise = null;
  }

  async request(path, { token, headers = {}, ...options } = {}) {
    if (!this.fetchImpl) {
      throw uploadError("CAPTURE_FETCH_UNAVAILABLE", "Fetch is unavailable.");
    }
    let response;
    try {
      response = await this.fetchImpl(path, {
        credentials: "same-origin",
        ...options,
        headers: {
          ...(token ? { Authorization: `Capture ${token}` } : {}),
          ...headers
        }
      });
    } catch (error) {
      throw uploadError(
        "CAPTURE_NETWORK",
        error?.message || "Capture network request failed."
      );
    }
    const body = await responseBody(response);
    if (!response.ok) {
      throw uploadError(
        body?.code || `HTTP_${response.status}`,
        body?.error || "Capture request failed.",
        response.status
      );
    }
    return body;
  }

  async authorize({ force = false } = {}) {
    if (
      !force &&
      this.authorization &&
      Date.parse(this.authorization.tokenExpiresAt) > this.now() + 30_000
    ) {
      return this.authorization;
    }
    const authorization = await this.request("/api/capture/sessions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}"
    });
    this.authorization = authorization;
    await this.store.putCapture({
      captureId: authorization.captureId,
      state: authorization.captureState || "active",
      maxPendingBytes: authorization.maxPendingBytes,
      updatedAt: this.now()
    });
    return authorization;
  }

  async withFreshToken(operation) {
    let authorization = await this.authorize();
    try {
      return await operation(authorization);
    } catch (error) {
      if (error.status !== 401) throw error;
      authorization = await this.authorize({ force: true });
      return operation(authorization);
    }
  }

  async registerEpoch(epoch) {
    return this.withFreshToken((authorization) =>
      this.request(
        `/api/capture/sessions/${encodeURIComponent(authorization.captureId)}/epochs`,
        {
          method: "POST",
          token: authorization.uploadToken,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            epochId: epoch.epochId,
            mimeType: epoch.mimeType,
            startedWallMs: epoch.startedWallMs
          })
        }
      )
    );
  }

  async uploadChunk(chunk) {
    return this.withFreshToken((authorization) => {
      if (authorization.captureId !== chunk.captureId) {
        throw uploadError(
          "CAPTURE_BINDING_MISMATCH",
          "Local capture does not match the current call."
        );
      }
      return this.request(
        `/api/capture/sessions/${encodeURIComponent(chunk.captureId)}` +
          `/epochs/${encodeURIComponent(chunk.epochId)}` +
          `/chunks/${encodeURIComponent(chunk.sequence)}`,
        {
          method: "PUT",
          token: authorization.uploadToken,
          headers: {
            "Content-Type": "application/octet-stream",
            "X-Liuzhu-Capture-Sha256": chunk.sha256,
            "X-Liuzhu-Capture-Start-Ms": String(chunk.startMs),
            "X-Liuzhu-Capture-End-Ms": String(chunk.endMs),
            "X-Liuzhu-Capture-Mime-Type": chunk.mimeType
          },
          body: chunk.blob
        }
      );
    });
  }

  async drain() {
    if (this.drainPromise) return this.drainPromise;
    this.drainPromise = this.drainInternal().finally(() => {
      this.drainPromise = null;
    });
    return this.drainPromise;
  }

  async drainInternal() {
    if (!this.online()) {
      this.onState("pending_upload");
      return { pending: true, uploaded: 0 };
    }
    const authorization = await this.authorize();
    const epochs = await this.store.listEpochs(authorization.captureId);
    const epochsById = new Map(epochs.map((epoch) => [epoch.epochId, epoch]));
    const registeredEpochs = new Set();
    let uploaded = 0;
    while (this.online()) {
      const pending = await this.store.listPending(authorization.captureId);
      if (!pending.length) break;
      const chunk = pending[0];
      try {
        const epoch = epochsById.get(chunk.epochId);
        if (!epoch) {
          throw uploadError(
            "CAPTURE_LOCAL_EPOCH_MISSING",
            "Local capture epoch is missing."
          );
        }
        if (!registeredEpochs.has(epoch.epochId)) {
          await this.registerEpoch(epoch);
          registeredEpochs.add(epoch.epochId);
        }
        this.onState("uploading");
        const receipt = await this.uploadChunk(chunk);
        await this.store.markUploaded(
          chunk.captureId,
          chunk.epochId,
          chunk.sequence,
          receipt
        );
        uploaded += 1;
      } catch (error) {
        if (error.code === "CAPTURE_NETWORK" || !this.online()) {
          this.onState("pending_upload");
          return { pending: true, uploaded };
        }
        if (error.status === 423) this.onState("blocked", error);
        throw error;
      }
    }
    const remaining = await this.store.listPending(authorization.captureId);
    this.onState(remaining.length ? "pending_upload" : "synced");
    return { pending: remaining.length > 0, uploaded };
  }

  async sealEpoch(epoch) {
    await this.registerEpoch(epoch);
    return this.withFreshToken((authorization) =>
      this.request(
        `/api/capture/sessions/${encodeURIComponent(authorization.captureId)}` +
          `/epochs/${encodeURIComponent(epoch.epochId)}/seal`,
        {
          method: "POST",
          token: authorization.uploadToken,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            lastSequence: epoch.lastSequence,
            endedWallMs: epoch.endedWallMs,
            durationMs: epoch.durationMs,
            gaps: epoch.gaps || []
          })
        }
      )
    );
  }

  async recoverUnsealedEpochs({ onlyEpochIds = null } = {}) {
    const authorization = await this.authorize();
    const epochs = await this.store.listEpochs(authorization.captureId);
    const selected = onlyEpochIds
      ? new Set([...onlyEpochIds].map(String))
      : null;
    const unsealed = epochs.filter(
      (item) =>
        item.state !== "sealed" &&
        (!selected || selected.has(String(item.epochId)))
    );
    for (const epoch of unsealed) {
      await this.registerEpoch(epoch);
    }
    await this.drain();
    for (const epoch of unsealed) {
      const durationMs = Math.max(0, Number(epoch.lastEndMs) || 0);
      const gaps = [...(epoch.gaps || [])];
      gaps.push({
        reason: "page_reloaded",
        startMs: durationMs,
        endMs: durationMs
      });
      await this.sealEpoch({
        ...epoch,
        lastSequence: Number.isInteger(epoch.lastSequence)
          ? epoch.lastSequence
          : -1,
        endedWallMs: Math.max(
          Number(epoch.startedWallMs),
          Number(epoch.startedWallMs) + durationMs
        ),
        durationMs,
        gaps: gaps.slice(-32)
      });
      await this.store.sealEpoch(authorization.captureId, epoch.epochId);
    }
  }

  async finalizeIfDrained() {
    const authorization = await this.authorize();
    const pending = await this.store.listPending(authorization.captureId);
    if (pending.length) return { pending: true, manifest: null };
    const manifest = await this.withFreshToken((fresh) =>
      this.request(
        `/api/capture/sessions/${encodeURIComponent(fresh.captureId)}/finalize`,
        {
          method: "POST",
          token: fresh.uploadToken,
          headers: { "Content-Type": "application/json" },
          body: "{}"
        }
      )
    );
    return { pending: false, manifest };
  }
}
